import React, { useEffect, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { GlobalModal } from "../../../../global/GlobalModal";
import { GlobalButton } from "../../../../global/forms/GlobalFormFields";
import { getScheduleById } from "../../api/schedulesApi";
import { safeErrorText, formatCreatedByDisplay } from "../../utils/scheduleHelpers";
import ScheduleStatusBadge from "../directory/ScheduleStatusBadge";

function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = parseISO(String(v).slice(0, 10));
    return isValid(d) ? format(d, "dd MMM yyyy") : String(v).slice(0, 10);
  } catch {
    return String(v).slice(0, 10);
  }
}

function fmtTime(v) {
  if (!v) return "—";
  const s = String(v);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function InfoRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "0.5rem 0",
        borderBottom: "1px solid #f1f5f9",
        gap: "1rem",
      }}
    >
      <span style={{ fontWeight: 600, color: "#64748b", fontSize: "0.875rem" }}>{label}</span>
      <span
        style={{
          fontWeight: 500,
          color: "#1e293b",
          fontSize: "0.875rem",
          textAlign: "right",
          maxWidth: "60%",
        }}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

export default function ViewScheduleModal({ scheduleId, onClose }) {
  const open = Boolean(scheduleId);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setSchedule(null);
      setError("");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getScheduleById(scheduleId);
        if (!cancelled) setSchedule(res?.data || null);
      } catch (err) {
        if (!cancelled) setError(safeErrorText(err, "Failed to load schedule"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, scheduleId]);

  return (
    <GlobalModal
      open={open}
      onClose={onClose}
      title="Schedule details"
      size="md"
      footer={
        <GlobalButton variant="ghost" onClick={onClose}>
          Close
        </GlobalButton>
      }
    >
      {loading ? <p className="schedule-table-loading">Loading…</p> : null}
      {error ? <p style={{ color: "#b91c1c" }}>{error}</p> : null}
      {schedule ? (
        <div>
          <InfoRow label="Schedule ID" value={schedule.schedule_code || schedule.schedule_id} />
          <InfoRow label="Title" value={schedule.schedule_title} />
          <InfoRow label="Type" value={schedule.schedule_type_name} />
          <InfoRow label="Date" value={fmtDate(schedule.schedule_date)} />
          <InfoRow label="Time" value={fmtTime(schedule.schedule_time)} />
          <InfoRow label="Location" value={schedule.location} />
          <InfoRow label="Description" value={schedule.description} />
          <InfoRow label="Assigned To" value={schedule.assigned_to_name} />
          <InfoRow label="Priority" value={schedule.priority_name} />
          <InfoRow
            label="Status"
            value={<ScheduleStatusBadge schedule={schedule} />}
          />
          <InfoRow label="Reminder" value={schedule.reminder ? "Yes" : "No"} />
          <InfoRow label="Remarks" value={schedule.remarks} />
          <InfoRow label="Created By" value={formatCreatedByDisplay(schedule)} />
        </div>
      ) : null}
    </GlobalModal>
  );
}
